import { DataInput, DataInputType, DatasetType, StepType } from '@/types/automationFields.ts';
import { LocalSubflowCallStep } from '@/types/automationSteps.ts';
import { AutomationLocalSubflowOutputPort } from '@/types/automations.ts';

export const newLocalSubflowId = (prefix: string) =>
  `${prefix}_${crypto.randomUUID().replaceAll('-', '').slice(0, 12)}`;

export const newPortId = () => newLocalSubflowId('port');

/** Sentinel used only in select values; the persisted default output id is null. */
export const DEFAULT_LOCAL_SUBFLOW_SOURCE_OUTPUT_ID = '__default_output__';

export const emptyLocalSubflowInput = (datasetType: DatasetType): DataInput => ({
  name: null,
  last_known_display_name: null,
  data_input_type: DataInputType.Empty,
  config: { dataset_type: datasetType },
});

type LocalSubflowPortInput = DataInput & {
  data_input_type: DataInputType.LocalSubflowPort;
  config: { port_id: string };
};

const isLocalSubflowPortInput = (input: DataInput): input is LocalSubflowPortInput =>
  input.data_input_type === DataInputType.LocalSubflowPort;

/** Returns the first module-port assignment represented by a regular input group. */
export const assignedLocalSubflowPortId = (inputs: DataInput[]) => {
  const input = inputs.find(isLocalSubflowPortInput);
  return input?.config.port_id ?? null;
};

/**
 * Replaces one module-port assignment without discarding unrelated inputs.
 *
 * The overview inspector intentionally offers one focused module-input selector;
 * multi-input editing remains available through the normal step form.
 */
export const replaceAssignedLocalSubflowPort = (
  inputs: DataInput[],
  portId: string | null,
): DataInput[] => {
  const currentIndex = inputs.findIndex(isLocalSubflowPortInput);
  if (!portId) {
    return currentIndex < 0 ? inputs : inputs.filter((_, index) => index !== currentIndex);
  }

  const nextInput: DataInput = {
    name: null,
    last_known_display_name: null,
    data_input_type: DataInputType.LocalSubflowPort,
    config: { port_id: portId },
  };
  if (currentIndex < 0) return [...inputs, nextInput];
  return inputs.map((input, index) => index === currentIndex ? nextInput : input);
};

export const callersFor = (subflowId: string, steps: unknown[] | undefined) =>
  (steps ?? []).filter((step): step is LocalSubflowCallStep =>
    typeof step === 'object' && step !== null &&
    (step as LocalSubflowCallStep).step_type === 'local_subflow_call' &&
    (step as LocalSubflowCallStep).subflow_id === subflowId,
  );

export const localSubflowDatasetOptions = Object.values(DatasetType)
  .filter((type) => type !== DatasetType.Unknown)
  .map((value) => ({ value, label: value }));

export const outputBindingsFor = (
  ports: AutomationLocalSubflowOutputPort[],
  existing: LocalSubflowCallStep['output_bindings'] = [],
) => ports.map((port) => ({
  ...(existing.find((binding) => binding.port_id === port.port_id) ?? {
    port_id: port.port_id,
    output_id: newLocalSubflowId('local_output'),
    output_name: port.name || 'Module output',
  }),
  dataset_type: port.dataset_type,
  schema_ref: port.schema_ref ?? null,
}));

const FORGE_MANAGED_MODULE_STRUCTURE_STEP_TYPES = new Set<StepType>([
  StepType.Conditional,
  StepType.Repeat,
  StepType.Retry,
  StepType.FailureHandler,
]);

export const isForgeManagedModuleStructure = (stepType: StepType) =>
  FORGE_MANAGED_MODULE_STRUCTURE_STEP_TYPES.has(stepType);
